import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  UserIcon,
  MapPinIcon,
  ChatBubbleLeftIcon,
  ShareIcon,
  Cog6ToothIcon,
  CameraIcon,
  SparklesIcon,
  BookOpenIcon,
  FireIcon,
  StarIcon,
  CheckBadgeIcon,
  PencilIcon,
  PhotoIcon,
  VideoCameraIcon
} from '@heroicons/react/24/outline'
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid'

const ProfilePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'posts' | 'stories' | 'media' | 'about'>('posts')
  const [isFollowing, setIsFollowing] = useState(false)

  const userStats = {
    followers: 12500,
    following: 856,
    likes: 45200,
    stories: 42
  }

  const posts = [
    { id: 1, type: 'image', likes: 234, comments: 45 },
    { id: 2, type: 'video', likes: 567, comments: 89 },
    { id: 3, type: 'image', likes: 123, comments: 23 },
    { id: 4, type: 'image', likes: 890, comments: 156 },
    { id: 5, type: 'video', likes: 445, comments: 67 },
    { id: 6, type: 'image', likes: 678, comments: 92 }
  ]

  return (
    <div className="space-y-6">
      {/* Cover Photo */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative h-64 rounded-3xl overflow-hidden bg-gradient-to-r from-primary-500/20 to-secondary-500/20"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50" />
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="absolute bottom-4 right-4 btn-glass flex items-center space-x-2"
        >
          <CameraIcon className="h-5 w-5" />
          <span>Edit Cover</span>
        </motion.button>
      </motion.div>

      {/* Profile Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card-glass p-8"
      >
        <div className="flex flex-col md:flex-row items-start md:items-end space-y-4 md:space-y-0 md:space-x-6">
          {/* Avatar */}
          <div className="relative -mt-20">
            <div className="w-32 h-32 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600 p-1">
              <div className="w-full h-full rounded-full bg-dark-100 flex items-center justify-center">
                <UserIcon className="h-16 w-16 text-white/50" />
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="absolute bottom-0 right-0 p-2 bg-primary-600 rounded-full"
            >
              <CameraIcon className="h-5 w-5" />
            </motion.button>
          </div>

          {/* User Info */}
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h1 className="text-3xl font-bold">Sarah Anderson</h1>
              <CheckBadgeIcon className="h-7 w-7 text-primary-400" />
              <span className="px-3 py-1 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-full text-sm font-medium">
                Premium
              </span>
            </div>
            <p className="text-white/70 mb-3">
              Travel enthusiast 🌍 | Coffee lover ☕ | Making memories one story at a time ✨
            </p>
            <div className="flex items-center space-x-4 text-sm text-white/70">
              <div className="flex items-center space-x-1">
                <MapPinIcon className="h-4 w-4" />
                <span>New York, USA</span>
              </div>
              <div className="flex items-center space-x-1">
                <FireIcon className="h-4 w-4 text-orange-400" />
                <span>28 day streak</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsFollowing(!isFollowing)}
              className={isFollowing ? 'btn-glass' : 'btn-primary'}
            >
              {isFollowing ? 'Following' : '+ Follow'}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-glass"
            >
              <ChatBubbleLeftIcon className="h-5 w-5" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-glass"
            >
              <ShareIcon className="h-5 w-5" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-glass"
            >
              <Cog6ToothIcon className="h-5 w-5" />
            </motion.button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mt-8 pt-6 border-t border-white/10">
          <StatItem label="Followers" value={userStats.followers.toLocaleString()} />
          <StatItem label="Following" value={userStats.following.toLocaleString()} />
          <StatItem label="Likes" value={userStats.likes.toLocaleString()} />
          <StatItem label="Stories" value={userStats.stories} />
        </div>
      </motion.div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Sidebar */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-1 space-y-6"
        >
          {/* About */}
          <div className="card-glass p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center justify-between">
              <span>About</span>
              <PencilIcon className="h-5 w-5 text-white/50 cursor-pointer hover:text-white" />
            </h2>
            <div className="space-y-4 text-sm">
              <InfoItem label="Age" value="28" />
              <InfoItem label="Gender" value="Female" />
              <InfoItem label="Relationship" value="Single" />
              <InfoItem label="Interests" value="Travel, Photography" />
              <InfoItem label="Languages" value="English, Spanish" />
              <InfoItem label="Member Since" value="January 2024" />
            </div>
          </div>

          {/* Badges */}
          <div className="card-glass p-6">
            <h2 className="text-xl font-bold mb-4">Badges</h2>
            <div className="grid grid-cols-3 gap-3">
              <BadgeItem icon={StarIcon} label="Top Creator" color="text-yellow-400" />
              <BadgeItem icon={HeartSolid} label="Match Maker" color="text-red-400" />
              <BadgeItem icon={FireIcon} label="30 Day" color="text-orange-400" />
              <BadgeItem icon={CheckBadgeIcon} label="Verified" color="text-primary-400" />
              <BadgeItem icon={SparklesIcon} label="Premium" color="text-purple-400" />
              <BadgeItem icon={BookOpenIcon} label="Author" color="text-blue-400" />
            </div>
          </div>

          {/* Interests */}
          <div className="card-glass p-6">
            <h2 className="text-xl font-bold mb-4">Interests</h2>
            <div className="flex flex-wrap gap-2">
              {['Travel', 'Photography', 'Cooking', 'Yoga', 'Music', 'Art'].map((interest) => (
                <span
                  key={interest}
                  className="px-3 py-1 bg-primary-500/20 text-primary-300 rounded-full text-sm"
                >
                  {interest}
                </span>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="lg:col-span-2 space-y-6"
        >
          {/* Tabs */}
          <div className="flex space-x-2 border-b border-white/10">
            {(['posts', 'stories', 'media', 'about'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 font-medium transition-all capitalize relative ${activeTab === tab ? 'text-white' : 'text-white/70 hover:text-white'
                  }`}
              >
                {tab}
                {activeTab === tab && (
                  <motion.div
                    layoutId="activeProfileTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-600 to-secondary-600"
                  />
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'posts' && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {posts.map((post, index) => (
                <PostCard key={post.id} post={post} index={index} />
              ))}
            </div>
          )}

          {activeTab === 'stories' && (
            <div className="space-y-4">
              <StoryItem title="Summer Adventures 2026" chapters={12} likes={1420} views={8500} />
              <StoryItem title="Finding Love in Paris" chapters={8} likes={892} views={4200} />
              <StoryItem title="Self-Discovery Journey" chapters={15} likes={2156} views={12300} />
            </div>
          )}

          {activeTab === 'media' && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((item, index) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.05 }}
                  className="aspect-square rounded-xl bg-gradient-to-br from-primary-500/20 to-secondary-500/20 flex items-center justify-center cursor-pointer"
                >
                  <PhotoIcon className="h-12 w-12 text-white/30" />
                </motion.div>
              ))}
            </div>
          )}

          {activeTab === 'about' && (
            <div className="card-glass p-6 space-y-6">
              <div>
                <h3 className="text-lg font-bold mb-3">Bio</h3>
                <p className="text-white/70 leading-relaxed">
                  Passionate about life and adventure. Always looking for meaningful connections.
                </p>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  )
}

const StatItem: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="text-center">
    <p className="text-2xl font-bold text-primary-400">{value}</p>
    <p className="text-sm text-white/70">{label}</p>
  </div>
)

const InfoItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-white/50">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
)

const BadgeItem: React.FC<{ icon: React.ComponentType<any>; label: string; color: string }> = ({ icon: Icon, label, color }) => (
  <motion.div
    whileHover={{ scale: 1.1 }}
    className="flex flex-col items-center space-y-1 p-2 bg-white/5 rounded-xl cursor-pointer"
  >
    <Icon className={`h-6 w-6 ${color}`} />
    <span className="text-xs text-center">{label}</span>
  </motion.div>
)

const PostCard: React.FC<{ post: any; index: number }> = ({ post, index }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: index * 0.05 }}
    whileHover={{ scale: 1.05 }}
    className="aspect-square rounded-xl bg-gradient-to-br from-primary-500/20 to-secondary-500/20 relative group cursor-pointer overflow-hidden"
  >
    <div className="absolute inset-0 flex items-center justify-center">
      {post.type === 'video' ? (
        <VideoCameraIcon className="h-12 w-12 text-white/30" />
      ) : (
        <PhotoIcon className="h-12 w-12 text-white/30" />
      )}
    </div>
    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-6">
      <div className="flex items-center space-x-2">
        <HeartSolid className="h-6 w-6 text-white" />
        <span className="font-bold">{post.likes}</span>
      </div>
      <div className="flex items-center space-x-2">
        <ChatBubbleLeftIcon className="h-6 w-6 text-white" />
        <span className="font-bold">{post.comments}</span>
      </div>
    </div>
  </motion.div>
)

const StoryItem: React.FC<{ title: string; chapters: number; likes: number; views: number }> = ({ title, chapters, likes, views }) => (
  <motion.div
    whileHover={{ scale: 1.02, x: 4 }}
    className="card-glass p-6 cursor-pointer"
  >
    <div className="flex items-center space-x-4">
      <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-primary-500/20 to-secondary-500/20 flex items-center justify-center flex-shrink-0">
        <BookOpenIcon className="h-8 w-8 text-primary-400" />
      </div>
      <div className="flex-1">
        <h3 className="font-bold text-lg mb-2">{title}</h3>
        <div className="flex items-center space-x-4 text-sm text-white/70">
          <span>{chapters} chapters</span>
          <span>•</span>
          <div className="flex items-center space-x-1">
            <HeartSolid className="h-4 w-4 text-red-400" />
            <span>{likes}</span>
          </div>
          <span>•</span>
          <span>{views.toLocaleString()} views</span>
        </div>
      </div>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="btn-glass px-4 py-2"
      >
        Read
      </motion.button>
    </div>
  </motion.div>
)

export default ProfilePage
